import type {
  ChronicleDeviceRecordingInfo,
  ChronicleLiveOffline,
} from '@/atoms/chronicle';
import { store } from '@/store';
import type { RecordingRecoveredEvent } from '@/tauri/chronicle/events';

export interface RecordingRecoveredContext {
  recordingInfo: ChronicleDeviceRecordingInfo | null;
  liveOffline: ChronicleLiveOffline | null;
  currentSessionId: string | null;
  /** Connected device id (`chronicleDeviceStateAtom.deviceId`) — the recovered
   *  event carries none, so a flow-2 build (no base info) sources it here. */
  deviceId: string | null;
  /** Wall-clock now in ms; a flow-2 build derives `startedAt` as
   *  `nowMs - durationSecs` so this stays a clock-free pure function. */
  nowMs: number;
}

export type RecordingRecoveredResult =
  /** Stale recovery (APP-7879): the event's fileName no longer matches the
   *  live recording — a late bind for a stopped file must not merge its
   *  session onto the new recording. All effects are skipped. */
  | { action: 'ignore' }
  | {
      action: 'apply';
      /** True when the emit carries a bound server session (`event.sessionId`
       *  non-null). Deferred/failed binds are unbound: the caller clears the
       *  hold but must not touch the session id or seed the timer (APP-7879). */
      bound: boolean;
      /** Resolved session id; never downgrades an existing non-null id to null. */
      sessionId: string | null;
      /** Recording-info update: merged onto the base when one exists (flow 1's
       *  `recording` state event created it), BUILT from the event payload on a
       *  bound emit with no base (flow 2 — an already-recording device
       *  serializes as `connected`, never `recording`, so no base ever forms).
       *  Null = leave the atom untouched (unbound emit with no base). */
      recordingInfo: ChronicleDeviceRecordingInfo | null;
      seedSecs: number;
      isPaused: boolean;
    };

/** Decide how a `chronicle:recording_recovered` event restores FE state.
 *  Pure function, mirroring `processRecordingDrop`'s pattern. Any applied
 *  emit (deferred/failed bind included) means the device is reconnected and
 *  controllable — the caller clears the live-offline hold (APP-7868). */
export function processRecordingRecovered(
  event: RecordingRecoveredEvent,
  context: RecordingRecoveredContext
): RecordingRecoveredResult {
  const base = context.recordingInfo;
  if (event.fileName && base?.fileName && event.fileName !== base.fileName) {
    return { action: 'ignore' };
  }
  return {
    action: 'apply',
    bound: event.sessionId != null,
    sessionId:
      event.sessionId ??
      context.currentSessionId ??
      context.liveOffline?.sessionId ??
      null,
    recordingInfo: base
      ? {
          ...base,
          sessionId: event.sessionId ?? base.sessionId,
          isPaused: event.isPaused,
          fileName: base.fileName ?? context.liveOffline?.fileName ?? undefined,
        }
      : buildRecordingInfoFromEvent(event, context),
    seedSecs: event.durationSecs,
    isPaused: event.isPaused,
  };
}

/** flow 2 (pure-offline connect): build the recording-info binding
 *  from the recovered event itself. Only a BOUND emit builds — seeding an
 *  unbound (deferred/failed) one would hand `useChronicleSessionControl`'s
 *  start detector a null→non-null transition with no `chronicleSessionIdAtom`
 *  to skip on, and it would create a duplicate session. The deferred retry
 *  re-emits with the real session id and the build completes then. */
function buildRecordingInfoFromEvent(
  event: RecordingRecoveredEvent,
  context: RecordingRecoveredContext
): ChronicleDeviceRecordingInfo | null {
  if (event.sessionId == null) return null;
  return {
    deviceId: context.deviceId ?? '',
    sessionId: event.sessionId,
    // Conservative derivation — the event carries only the elapsed duration.
    startedAt: new Date(context.nowMs - event.durationSecs * 1000).toISOString(),
    isPaused: event.isPaused,
    fileName: event.fileName || context.liveOffline?.fileName || undefined,
  };
}

/** Re-seed the timer to the device's authoritative duration. Pause first —
 *  `Timer.start()` no-ops while already running, so a bare `onStart` would not
 *  re-seed on duplicate recovered emits. Paused recoveries seed then freeze. */
export function seedRecoveredTimer(seedSecs: number, isPaused: boolean): void {
  store.dispatch.timerModel.onPause();
  store.dispatch.timerModel.onStart(seedSecs);
  if (isPaused) {
    store.dispatch.timerModel.onPause();
  }
}
